/** Backend TrackMatchStatus seeder ids (PHP constants). */
export const TRACK_MATCH_STATUS_HAS_MATCHES = 2;
export const TRACK_MATCH_STATUS_NO_MATCHES = 3;
/** Formerly "Done" in product copy; DB label is now Linked. */
export const TRACK_MATCH_STATUS_LINKED = 4;
/**
 * Set-aside state. Hides candidate match rows like NEVER used to; the BE pipeline auto-revives
 * the row to HAS_MATCHES if a new candidate appears in stored matches (SAU-175).
 */
export const TRACK_MATCH_STATUS_LATER = 5;
/**
 * @deprecated SAU-175 — LATER is the active set-aside status going forward.
 * NEVER is kept in the schema for FK integrity; run `one_off:sau-175:migrate-never-track-match-status-to-later`
 * to drain any remaining tracks. Retained here so lingering NEVER rows display correctly
 * rather than falling back to "Status 6".
 */
export const TRACK_MATCH_STATUS_NEVER = 6;
/** Queued or running live track action work (transient until the job finishes). */
export const TRACK_MATCH_STATUS_PROCESSING = 7;

/**
 * Aligns with backend TrackMatchStatus seeder ids.
 * NEVER (6) is @deprecated since SAU-175 but kept here for display and filter back-compat
 * until all tracks are re-pointed via `one_off:sau-175:migrate-never-track-match-status-to-later`.
 */
export const TRACK_MATCH_STATUS_FILTER_OPTIONS = [
  { id: 1, display_string: 'Not run' },
  { id: 2, display_string: 'Has matches' },
  { id: 3, display_string: 'No matches' },
  { id: 4, display_string: 'Linked' },
  { id: 5, display_string: 'Later' },
  { id: 6, display_string: 'Never' },
  { id: 7, display_string: 'Processing' },
] as const;

/** Hide candidate match rows when the live track is in this review state (from persisted or in-memory match data). */
export function trackMatchingStatusHidesCandidates(statusId: number | undefined): boolean {
  if (statusId === undefined) return false;
  // NEVER (deprecated) is intentionally included so lingering NEVER tracks still collapse correctly.
  return (
    statusId === TRACK_MATCH_STATUS_LINKED ||
    statusId === TRACK_MATCH_STATUS_LATER ||
    statusId === TRACK_MATCH_STATUS_NEVER
  );
}

/** Live track action queued or running — use for busy UI or labels (not a Linked/Never review state). */
export function isTrackMatchStatusProcessing(statusId: number | undefined): boolean {
  return statusId === TRACK_MATCH_STATUS_PROCESSING;
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

export function displayStringForTrackMatchStatusId(statusId: unknown): string {
  const id = Number(statusId);
  if (!Number.isFinite(id)) return '—';
  const hit = TRACK_MATCH_STATUS_FILTER_OPTIONS.find((row) => row.id === id);
  return hit?.display_string ?? `Status ${id}`;
}

/** Nested shape on LiveTrack for `track_match_status.display_string` columns. */
export function trackMatchStatusRelationFromId(
  statusId: unknown
): { id: number; display_string: string } | undefined {
  const id = Number(statusId);
  if (!Number.isFinite(id)) return undefined;
  return { id, display_string: displayStringForTrackMatchStatusId(id) };
}

/**
 * Prefer top-level `track_match_status_id` (DB FK); if missing, use `track_match_status.id`.
 * Avoids treating a stale nested relation as authoritative when the FK says otherwise.
 */
export function getEffectiveTrackMatchStatusId(
  row: Record<string, unknown> | undefined
): number | undefined {
  if (!row) return undefined;
  const top = row.track_match_status_id;
  if (top !== undefined && top !== null && top !== '') {
    const n = Number(top);
    if (Number.isFinite(n)) return n;
  }
  const nested = row.track_match_status;
  if (isPlainRecord(nested) && nested.id !== undefined && nested.id !== null) {
    const n = Number(nested.id);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}
